import { useEffect, useRef } from 'react';
import { useAuthenticatedApiFetch } from './useAuthenticatedApiFetch';
import ApiConstants from '../utils/ApiConstants';

const STYLE_TAG_ID = 'saleflow-design-override';

/**
 * Fetches the CSS design override for a saleflow and injects it as a <style>
 * tag in <head>, overriding any CSS variables defined in the default stylesheet.
 *
 * The override style tag is removed on unmount / saleFlowId change so switching
 * between products always starts clean.
 */
export const useSaleFlowDesign = (saleFlowId) => {
  const authenticatedApiFetch = useAuthenticatedApiFetch();
  const fetchRef = useRef(authenticatedApiFetch);
  fetchRef.current = authenticatedApiFetch;

  useEffect(() => {
    if (!saleFlowId) return;

    let cancelled = false;

    const fetchDesign = async () => {
      const endpoint = ApiConstants.getSaleFlowDesignEndpoint(saleFlowId);
      const url = `${ApiConstants.BASE_URL}${endpoint}`;

      const result = await fetchRef.current(url, {}, 'json');

      if (cancelled || !result || result.status !== 200) return;

      const cssValue = result.data?.cssValue?.trim();

      if (!cssValue) return;

      let styleTag = document.getElementById(STYLE_TAG_ID);
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = STYLE_TAG_ID;
        document.head.appendChild(styleTag);
      }
      styleTag.textContent = cssValue;
    };

    fetchDesign();

    return () => {
      cancelled = true;
      document.getElementById(STYLE_TAG_ID)?.remove();
    };
  }, [saleFlowId]);
};
